import type { Pinia, PiniaPlugin } from 'pinia';
import { createPersistedState, type StorageLike } from 'pinia-plugin-persistedstate';
import { watch } from 'vue';
import { isDemoMode } from '~/services/demo/mode';
import { useAuthStore } from '~/stores/auth';

/**
 * Saved state that belongs to the member signed in on this browser.
 *
 * Saved state outlives the session that wrote it. Ending a session in hive clears it (`endSession`
 * in stores/auth.ts), but a session also ends where hive's code never runs — a sign-out on the auth
 * host or in another Privacy Bee app — and the next member to sign in here opens a new document
 * whose stores hydrate from whatever the last one left. So every entry names the member it belongs
 * to, and it hydrates only into that member's session. An entry any other member wrote is removed
 * without hydrating.
 *
 * The member is the one this document's `/me` confirmed (`getMe`, `refreshUser` in stores/auth.ts),
 * while the browser's session is still theirs. Until it has answered, and while the session is
 * lapsed or anyone else's, there is no member, and nothing hydrates, saves or is removed. A store
 * that hydrated then hydrates once there is a member: its page can open before the boot confirms
 * one, or before its first request renews a lapsed access token.
 *
 * This is the app's only persistence: plugins/member-persisted-state.ts installs
 * `memberPersistedState()` into the app's pinia, so a store that declares `persist`, in any form,
 * is member-scoped with nothing to opt into. stores/memberPersistence.spec.ts fails the suite on
 * any other use of pinia-plugin-persistedstate.
 */

/** A saved entry: the state as the plugin serialized it, and the member it belongs to. */
interface MemberEntry {
    member: number;
    state: string;
}

/**
 * The member whose state this document hydrates and saves.
 *
 * The auth cookies are shared by every tab, so a sign-in or sign-out elsewhere changes whose session
 * the browser holds while this document still shows the member its /me confirmed, before it hears of
 * the change: whatever is typed into it from then on is not that member's to save.
 *
 * A demo has no member. Its identity is a fixture whose id is some real member's too, and the
 * storage it would read and write belongs to whoever signs in on this browser for real.
 */
function signedInMember(pinia: Pinia): number | null {
    if (isDemoMode()) return null;

    const auth = useAuthStore(pinia);
    const member = auth.user?.identity.id;
    if (member === undefined) return null;

    return auth.sessionMemberId() === member ? member : null;
}

function readEntry(raw: string): MemberEntry | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null) return null;

    const { member, state } = parsed as Record<string, unknown>;

    return typeof member === 'number' && typeof state === 'string' ? { member, state } : null;
}

/** Where one store's state is saved, and whether it has hydrated for a member yet. */
interface MemberStorage extends StorageLike {
    /** Whether an entry of the store hydrated while no member was signed in here. */
    hydratedForNoMember(): boolean;
}

/**
 * Where one store's state is saved, for the document whose pinia holds it.
 *
 * Once a store has hydrated for a member, it holds that member's state for the life of the
 * document, so it saves only in their name: a member no longer signed in here is saved nothing for,
 * and neither is anyone else this document comes to confirm without reloading. A store that hydrated
 * for no member saves nothing until it hydrates for one.
 */
function memberStorage(pinia: Pinia): MemberStorage {
    // By storage key: a store can declare more than one persistence.
    const hydratedFor = new Map<string, number | null>();

    return {
        hydratedForNoMember() {
            return [...hydratedFor.values()].includes(null);
        },

        getItem(key) {
            const member = signedInMember(pinia);
            hydratedFor.set(key, member);
            if (member === null) return null;

            const raw = localStorage.getItem(key);
            if (raw === null) return null;

            const entry = readEntry(raw);
            if (entry?.member === member) return entry.state;

            // Another member's state, or state that names no member: it never hydrates here, and
            // nothing is served by leaving it for the next reader.
            localStorage.removeItem(key);
            return null;
        },

        setItem(key, state) {
            const member = signedInMember(pinia);
            if (member === null || hydratedFor.get(key) !== member) return;

            const entry: MemberEntry = { member, state };
            localStorage.setItem(key, JSON.stringify(entry));
        },
    };
}

/**
 * Pinia's persistence for this app: every store that declares `persist` saves its state in the name
 * of the signed-in member. A store names what to save and under which key, never where: storage of
 * its own would save outside the member's name, so such a store fails as it is created.
 */
export function memberPersistedState(): PiniaPlugin {
    return (context) => {
        const { persist } = context.options;
        if (!persist) return;

        const declared = Array.isArray(persist) ? persist : typeof persist === 'object' ? [persist] : [];
        if (declared.some((options) => options.storage !== undefined)) {
            throw new Error(
                `Store "${context.store.$id}" names its own storage; saved state is member-scoped (stores/memberPersistence.ts), so a store declares only what to save`
            );
        }

        const storage = memberStorage(context.pinia);
        // debug: a store that cannot hydrate or save says so in the console, rather than starting
        // empty in silence.
        createPersistedState({ storage, debug: true })(context);

        // Whether a member is signed in here changes with the identity /me confirms and with the
        // access token this document sends, which follows the session in the jar: a renewal here or
        // in another tab or app.
        const auth = useAuthStore(context.pinia);
        watch(
            () => [auth.user?.identity.id, auth.token],
            () => {
                if (storage.hydratedForNoMember() && signedInMember(context.pinia) !== null) {
                    context.store.$hydrate();
                }
            }
        );
    };
}
